import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Loader2, ArrowLeft, Eye, Upload } from "lucide-react";
import StatsSettings from "@/components/StatsSettings";
import LeadsAdmin from "@/components/LeadsAdmin";
import UsersAdmin from "@/components/UsersAdmin";
import StatisticsAdmin from "@/components/StatisticsAdmin";
import LandingsAdmin from "@/components/LandingsAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG_PRESETS = [
  "/assets/blog/locazione-quanto-si-guadagna-ad-affittare-casa-testata-tnb.jpg",
  "/assets/blog/legislazione-affitti-come-affittare-casa-a-stranieri-in-sicurezza-testata-tnb.jpg",
  "/assets/blog/legislazione-affitti-comproprietario-come-affittare-casa-testata-tnb.jpg",
  "/assets/blog/legislazione-affitti-chi-paga-spese-cancello-casa-in-affitto-testata-tnb.jpg",
  "/assets/blog/locazione-come-si-calcola-affitto-casa-testata-tnb.jpg",
  "/assets/blog/suggerimenti-affitto-casa-adatta-da-affittare-3-punti-principali-testata-tnb.jpg",
  "/assets/blog/legislazione-affitti-chi-paga-spurgo-fogna-casa-in-affitto-testata-tnb.jpg",
  "/assets/blog/suggerimenti-affitto-affittare-casa-da-privati-o-consulente-testata-tnb.jpg",
  "/assets/blog/suggerimenti-affitto-cosa-fare-in-caso-di-inquilino-moroso-testata-tnb.jpg",
  "/assets/blog/suggerimenti-affitto-come-trovare-inquilino-affidabile-per-affitto-testata-tnb.jpg",
  "/assets/hero-casa.jpg",
];

const CATEGORIES = ["Locazione", "Legislazione affitti", "Suggerimenti affitto", "News"];

const emptyForm = { title: "", slug: "", category: "Suggerimenti affitto", excerpt: "", body: "", img: "", read: "5 min", date: "", published: true };

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return "Errore imprevisto. Riprova.";
};

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("password");
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);

  const finish = (data) => {
    localStorage.setItem("sf-admin-token", data.token);
    toast.success(`Bentornato, ${data.user.name}!`);
    onSuccess(data.token);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.requires_2fa) {
        setStep("otp");
        setSetup(data.setup ? { qr: data.qr, secret: data.secret } : null);
        setCode("");
      } else if (data.token) {
        finish(data);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-2fa`, { email, code });
      finish(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="admin-login-page">
      {step === "otp" ? (
        <form onSubmit={submitOtp} data-testid="admin-otp-form" className="w-full max-w-sm rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-xl p-8">
          <img src="/assets/logo-nuovo.png" alt="Semplificaffitto.it" className="h-7 w-auto brightness-0 invert mb-8" />
          {setup ? (
            <>
              <h1 className="font-serif font-bold text-2xl text-white mb-1">Attiva Google Authenticator</h1>
              <p className="text-sm text-white/50 mb-5">
                1. Apri Google Authenticator sul telefono e tocca <span className="text-white/80">+</span> → <span className="text-white/80">Scansiona codice QR</span>
              </p>
              <div className="flex justify-center mb-4">
                <img src={setup.qr} alt="QR code Google Authenticator" data-testid="admin-otp-qr" className="w-44 h-44 rounded-xl bg-white p-2" />
              </div>
              <p className="text-xs text-white/40 mb-1">Non riesci a scansionare? Inserisci manualmente questa chiave:</p>
              <p className="text-xs font-mono text-white/70 break-all bg-white/5 rounded-lg px-3 py-2 mb-5 select-all" data-testid="admin-otp-secret">{setup.secret}</p>
              <p className="text-sm text-white/50 mb-4">2. Inserisci il codice a 6 cifre mostrato dall'app:</p>
            </>
          ) : (
            <>
              <h1 className="font-serif font-bold text-2xl text-white mb-1">Verifica in due passaggi</h1>
              <p className="text-sm text-white/50 mb-7">
                Apri Google Authenticator e inserisci il codice a 6 cifre per <span className="text-white/80">{email}</span>.
              </p>
            </>
          )}
          <input
            type="text"
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            minLength={6}
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="Codice a 6 cifre"
            data-testid="admin-otp-input"
            className="w-full mb-6 rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-center text-lg tracking-[0.5em] text-white placeholder:text-white/35 placeholder:text-sm placeholder:tracking-normal focus:outline-none focus:border-azure"
          />
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            data-testid="admin-otp-submit-btn"
            className="w-full inline-flex justify-center items-center gap-2 bg-azure hover:bg-brandblue disabled:opacity-60 text-white font-semibold rounded-full px-6 py-3.5 text-sm transition-colors"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Verifica e accedi
          </button>
          <button
            type="button"
            onClick={() => { setStep("password"); setPassword(""); setCode(""); setSetup(null); }}
            data-testid="admin-otp-back-btn"
            className="mt-5 inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
          >
            <ArrowLeft size={13} /> Torna al login
          </button>
        </form>
      ) : (
      <form onSubmit={submitPassword} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-xl p-8">
        <img src="/assets/logo-nuovo.png" alt="Semplificaffitto.it" className="h-7 w-auto brightness-0 invert mb-8" />
        <h1 className="font-serif font-bold text-2xl text-white mb-1">Area Admin</h1>
        <p className="text-sm text-white/50 mb-7">Gestione articoli del blog</p>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          data-testid="admin-email-input"
          className="w-full mb-4 rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-azure"
        />
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          data-testid="admin-password-input"
          className="w-full mb-6 rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-azure"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit-btn"
          className="w-full inline-flex justify-center items-center gap-2 bg-azure hover:bg-brandblue disabled:opacity-60 text-white font-semibold rounded-full px-6 py-3.5 text-sm transition-colors"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Accedi
        </button>
        <Link to="/" className="mt-5 inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors" data-testid="admin-back-home-link">
          <ArrowLeft size={13} /> Torna al sito
        </Link>
      </form>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("sf-admin-token") || "");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("articoli");
  const fileRef = useRef(null);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, auth());
      setEditor((ed) => ({ ...ed, form: { ...ed.form, img: data.url } }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [newLeads, setNewLeads] = useState(0);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog`, auth());
      setPosts(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("sf-admin-token");
        setToken("");
      } else {
        toast.error(errMsg(err));
      }
    } finally {
      setLoading(false);
    }
  }, [token, auth]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/leads/new-count`, auth())
      .then(({ data }) => setNewLeads(data.count))
      .catch(() => {});
  }, [token, tab, auth]);

  if (!token) return <Login onSuccess={setToken} />;

  const openNew = () => setEditor({ id: null, form: { ...emptyForm } });
  const openEdit = (p) =>
    setEditor({
      id: p.id,
      form: {
        title: p.title, slug: p.slug, category: p.category, excerpt: p.excerpt,
        body: Array.isArray(p.body) ? p.body.join("\n\n") : p.body || "",
        img: p.img, read: p.read, date: p.date || (p.created_at || "").slice(0, 10), published: p.published,
      },
    });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editor.id) {
        await axios.put(`${API}/admin/blog/${editor.id}`, editor.form, auth());
        toast.success("Articolo aggiornato");
      } else {
        await axios.post(`${API}/admin/blog`, editor.form, auth());
        toast.success("Articolo pubblicato");
      }
      setEditor(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${p.id}`, auth());
      toast.success("Articolo eliminato");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const logout = () => {
    localStorage.removeItem("sf-admin-token");
    setToken("");
  };

  const inputCls = "w-full rounded-xl bg-white border border-ink/15 px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-azure focus:ring-1 focus:ring-azure";

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-nuovo.png" alt="Semplificaffitto.it" className="h-6 w-auto brightness-0 invert" />
            <span className="text-xs uppercase tracking-[0.2em] text-white/50">Area Admin · Blog</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-white/60 hover:text-white transition-colors" data-testid="admin-view-site-link">
              Vedi il sito
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 text-xs border border-white/25 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          <button
            onClick={() => setTab("articoli")}
            data-testid="admin-tab-articoli"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "articoli" ? "bg-ink text-white" : "bg-white text-ink/60 border border-ink/15 hover:text-ink"
            }`}
          >
            Articoli
          </button>
          <button
            onClick={() => setTab("landing")}
            data-testid="admin-tab-landing"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "landing" ? "bg-ink text-white" : "bg-white text-ink/60 border border-ink/15 hover:text-ink"
            }`}
          >
            Landing
          </button>
          <button
            onClick={() => setTab("richieste")}
            data-testid="admin-tab-richieste"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "richieste" ? "bg-ink text-white" : "bg-white text-ink/60 border border-ink/15 hover:text-ink"
            }`}
          >
            Richieste
            {newLeads > 0 && (
              <span
                data-testid="admin-tab-richieste-badge"
                className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold align-middle"
              >
                {newLeads}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("statistiche")}
            data-testid="admin-tab-statistiche"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "statistiche" ? "bg-ink text-white" : "bg-white text-ink/60 border border-ink/15 hover:text-ink"
            }`}
          >
            Statistiche
          </button>
          <button
            onClick={() => setTab("utenti")}
            data-testid="admin-tab-utenti"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "utenti" ? "bg-ink text-white" : "bg-white text-ink/60 border border-ink/15 hover:text-ink"
            }`}
          >
            Utenti
          </button>
        </div>

        {tab === "utenti" ? (
          <UsersAdmin />
        ) : tab === "statistiche" ? (
          <StatisticsAdmin />
        ) : tab === "landing" ? (
          <LandingsAdmin />
        ) : tab === "richieste" ? (
          <LeadsAdmin />
        ) : (
          <>
        <StatsSettings />

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif font-bold text-3xl">Articoli del blog</h1>
            <p className="text-sm text-ink/50 mt-1">{posts.length} articoli · le modifiche sono subito visibili sul sito</p>
          </div>
          <button onClick={openNew} data-testid="admin-new-post-btn" className="inline-flex items-center gap-2 bg-azure hover:bg-brandblue text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors">
            <Plus size={16} /> Nuovo articolo
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-azure" />
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-posts-list">
            {posts.map((p) => (
              <div key={p.id} data-testid={`admin-post-row-${p.slug}`} className="flex items-center gap-4 bg-white rounded-2xl border border-ink/10 px-5 py-4">
                {p.img && <img src={p.img} alt="" className="w-16 h-12 object-cover rounded-lg hidden sm:block" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{p.title}</p>
                  <p className="text-xs text-ink/45 mt-0.5">
                    {p.category} · /blog/{p.slug}
                  </p>
                </div>
                <span className={`hidden sm:inline-flex text-[11px] font-semibold rounded-full px-3 py-1 ${p.published ? "bg-azure/10 text-brandblue" : "bg-ink/10 text-ink/50"}`}>
                  {p.published ? "Pubblicato" : "Bozza"}
                </span>
                <Link to={`/blog/${p.slug}`} target="_blank" className="p-2 text-ink/40 hover:text-brandblue transition-colors" aria-label="Anteprima" data-testid={`admin-preview-btn-${p.slug}`}>
                  <Eye size={17} />
                </Link>
                <button onClick={() => openEdit(p)} className="p-2 text-ink/40 hover:text-brandblue transition-colors" aria-label="Modifica" data-testid={`admin-edit-btn-${p.slug}`}>
                  <Pencil size={17} />
                </button>
                <button onClick={() => remove(p)} className="p-2 text-ink/40 hover:text-red-500 transition-colors" aria-label="Elimina" data-testid={`admin-delete-btn-${p.slug}`}>
                  <Trash2 size={17} />
                </button>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </main>

      {editor && (
        <div className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm overflow-y-auto" data-testid="admin-editor-modal">
          <div className="min-h-full flex items-start justify-center p-4 sm:p-8">
            <form onSubmit={save} data-testid="admin-post-form" className="w-full max-w-2xl bg-paper rounded-3xl shadow-2xl p-6 sm:p-8 my-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif font-bold text-2xl">{editor.id ? "Modifica articolo" : "Nuovo articolo"}</h2>
                <button type="button" onClick={() => setEditor(null)} className="text-ink/40 hover:text-ink text-sm" data-testid="admin-editor-close-btn">
                  Chiudi
                </button>
              </div>
              <div className="space-y-4">
                <input required value={editor.form.title} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, title: e.target.value } })} placeholder="Titolo dell'articolo *" className={inputCls} data-testid="admin-input-title" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input value={editor.form.slug} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, slug: e.target.value } })} placeholder="Slug URL (vuoto = automatico)" className={inputCls} data-testid="admin-input-slug" />
                  <select value={editor.form.category} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, category: e.target.value } })} className={inputCls} data-testid="admin-input-category">
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <textarea value={editor.form.excerpt} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, excerpt: e.target.value } })} placeholder="Estratto (anteprima nelle card e meta description)" rows={2} className={`${inputCls} resize-none`} data-testid="admin-input-excerpt" />
                <div>
                  <label className="block text-xs font-semibold text-ink/50 uppercase tracking-wider mb-2">Immagine di copertina</label>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid="admin-input-file" />
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="admin-upload-img-btn"
                      className="inline-flex items-center gap-2 bg-ink text-white text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-brandblue disabled:opacity-60 transition-colors"
                    >
                      {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                      {uploading ? "Caricamento..." : "Carica foto dal computer"}
                    </button>
                    <span className="text-xs text-ink/40">oppure incolla un URL qui sotto</span>
                  </div>
                  <input value={editor.form.img} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, img: e.target.value } })} placeholder="URL immagine (o usa il caricamento qui sopra)" list="img-presets" className={`${inputCls} mt-3`} data-testid="admin-input-img" />
                  <datalist id="img-presets">
                    {IMG_PRESETS.map((i) => (
                      <option key={i} value={i} />
                    ))}
                  </datalist>
                  {editor.form.img && (
                    <img src={editor.form.img} alt="Anteprima copertina" className="mt-3 h-28 rounded-xl object-cover border border-ink/10" data-testid="admin-img-preview" />
                  )}
                </div>
                <textarea value={editor.form.body} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, body: e.target.value } })} placeholder="Corpo dell'articolo. Separa i paragrafi con una riga vuota." rows={10} className={`${inputCls} resize-y`} data-testid="admin-input-body" />
                <p className="text-[11px] text-ink/40 mt-1.5">
                  Suggerimento SEO: inizia una riga con <strong>##</strong> per creare un sottotitolo (es. <em>## Come funziona</em>). I paragrafi si separano con una riga vuota.
                </p>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <input value={editor.form.read} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, read: e.target.value } })} placeholder="Tempo lettura (es. 5 min)" className={`${inputCls} max-w-[220px]`} data-testid="admin-input-read" />
                  <label className="inline-flex items-center gap-2 text-sm text-ink/70">
                    <span className="text-xs text-ink/45 uppercase tracking-wider">Data</span>
                    <input type="date" value={editor.form.date} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, date: e.target.value } })} className={`${inputCls} w-auto`} data-testid="admin-input-date" />
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-ink/70 cursor-pointer">
                    <input type="checkbox" checked={editor.form.published} onChange={(e) => setEditor({ ...editor, form: { ...editor.form, published: e.target.checked } })} className="w-4 h-4 accent-[#009DDF]" data-testid="admin-input-published" />
                    Pubblicato
                  </label>
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="admin-save-post-btn" className="mt-7 w-full inline-flex justify-center items-center gap-2 bg-azure hover:bg-brandblue disabled:opacity-60 text-white font-semibold rounded-full px-6 py-3.5 text-sm transition-colors">
                {saving && <Loader2 size={16} className="animate-spin" />}
                {editor.id ? "Salva modifiche" : "Pubblica articolo"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
